/* =========================================================
   FILE: cn-menu-drawer-membership-panel-blur.js
   Sofortiger lokaler Blur für Inhalte, die vom geöffneten
   Membership-Vorteils-Panel überdeckt werden.
========================================================= */

(() => {
  'use strict';

  const DRAWER_SELECTOR = '#menu-drawer';
  const BENEFITS_SELECTOR =
    '.cn-member-benefits--drawer, ' +
    '[data-cn-member-benefits].cn-member-benefits--drawer';
  const PANEL_SELECTOR =
    '[data-cn-member-benefits-panel], ' +
    '.cn-member-benefits__panel';
  const BUTTON_SELECTOR = '.cn-member-benefits__button';

  const BLUR_CLASS = 'cn-member-panel-overlap-blur';
  const LINE_BLUR_CLASS = 'cn-member-panel-line-overlap';
  const MOBILE_QUERY = window.matchMedia('(max-width: 989px)');
  const initialized = new WeakSet();

  const CANDIDATE_SELECTOR = [
    '.menu-drawer__menu-item',
    '.menu-drawer__close-button',
    '.menu-drawer__account',
    '.cn-member-benefits__button',
    '.menu-drawer__localization',
    '.menu-drawer .list-social__link'
  ].join(', ');

  function rectanglesOverlap(first, second, tolerance = 2) {
    return !(
      first.right <= second.left + tolerance ||
      first.left >= second.right - tolerance ||
      first.bottom <= second.top + tolerance ||
      first.top >= second.bottom - tolerance
    );
  }

  function isVisible(element) {
    if (!element || element.hidden) return false;

    const style = window.getComputedStyle(element);

    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      Number.parseFloat(style.opacity || '1') <= 0
    ) {
      return false;
    }

    return element.getClientRects().length > 0;
  }

  function isPanelOpen(benefits, panel) {
    return Boolean(
      MOBILE_QUERY.matches &&
      benefits &&
      panel &&
      benefits.classList.contains('is-open') &&
      !panel.hidden &&
      panel.getClientRects().length > 0
    );
  }

  function clearBlur(drawer) {
    drawer.querySelectorAll(`.${BLUR_CLASS}`).forEach((element) => {
      element.classList.remove(BLUR_CLASS);
    });

    drawer.querySelectorAll(`.${LINE_BLUR_CLASS}`).forEach((element) => {
      element.classList.remove(LINE_BLUR_CLASS);
    });
  }

  function shouldIgnoreCandidate(element, panel, benefits) {
    return Boolean(
      !element ||
      element === panel ||
      panel.contains(element) ||
      element.contains(panel) ||
      element === benefits ||
      element.contains(benefits)
    );
  }

  function applyBlurForRect(drawer, benefits, panel, panelRect) {
    clearBlur(drawer);

    if (!MOBILE_QUERY.matches || !panelRect) return;

    drawer.querySelectorAll(CANDIDATE_SELECTOR).forEach((element) => {
      if (
        shouldIgnoreCandidate(element, panel, benefits) ||
        !isVisible(element)
      ) {
        return;
      }

      if (rectanglesOverlap(element.getBoundingClientRect(), panelRect)) {
        element.classList.add(BLUR_CLASS);
      }
    });

    const utilityLinks = drawer.querySelector(
      '.menu-drawer__utility-links'
    );

    if (!utilityLinks) return;

    const utilityRect = utilityLinks.getBoundingClientRect();
    const lineY = utilityRect.top;
    const tolerance = 8;

    const overlapsHorizontally =
      panelRect.right > utilityRect.left &&
      panelRect.left < utilityRect.right;

    const overlapsLine =
      panelRect.top <= lineY + tolerance &&
      panelRect.bottom >= lineY - tolerance;

    utilityLinks.classList.toggle(
      LINE_BLUR_CLASS,
      overlapsHorizontally && overlapsLine
    );
  }

  function updateBlur(drawer, benefits, panel) {
    if (!isPanelOpen(benefits, panel)) {
      clearBlur(drawer);
      return;
    }

    applyBlurForRect(
      drawer,
      benefits,
      panel,
      panel.getBoundingClientRect()
    );
  }

  function measureClosedPanel(panel) {
    const wasHidden = panel.hidden;
    const previousStyle = panel.getAttribute('style');

    panel.hidden = false;
    panel.style.setProperty('display', 'grid', 'important');
    panel.style.setProperty('visibility', 'hidden', 'important');
    panel.style.setProperty('opacity', '0', 'important');
    panel.style.setProperty('pointer-events', 'none', 'important');
    panel.style.setProperty('transition', 'none', 'important');

    const rect = panel.getBoundingClientRect();

    if (previousStyle === null) {
      panel.removeAttribute('style');
    } else {
      panel.setAttribute('style', previousStyle);
    }

    panel.hidden = wasHidden;

    return rect.width > 0 && rect.height > 0 ? rect : null;
  }

  function initializeDrawer(drawer) {
    if (!drawer || initialized.has(drawer)) return;

    const benefits = drawer.querySelector(BENEFITS_SELECTOR);
    const panel = benefits?.querySelector(PANEL_SELECTOR);
    const button = benefits?.querySelector(BUTTON_SELECTOR);

    if (!benefits || !panel || !button) return;

    initialized.add(drawer);

    let animationFrameId = 0;
    let settleTimer = 0;

    const update = () => updateBlur(drawer, benefits, panel);

    const followTransition = (duration = 280) => {
      cancelAnimationFrame(animationFrameId);

      const start = performance.now();

      const step = (time) => {
        update();

        if (time - start < duration) {
          animationFrameId = requestAnimationFrame(step);
        }
      };

      animationFrameId = requestAnimationFrame(step);
    };

    const schedule = () => {
      update();
      queueMicrotask(update);

      requestAnimationFrame(() => {
        update();
        followTransition();
      });

      clearTimeout(settleTimer);
      settleTimer = window.setTimeout(update, 300);
    };

    button.addEventListener(
      'pointerdown',
      () => {
        if (!MOBILE_QUERY.matches) return;

        if (benefits.classList.contains('is-open')) {
          clearBlur(drawer);
          return;
        }

        const projectedRect =
          panel.getClientRects().length > 0
            ? panel.getBoundingClientRect()
            : measureClosedPanel(panel);

        applyBlurForRect(drawer, benefits, panel, projectedRect);
      },
      { passive: true }
    );

    button.addEventListener('click', schedule);

    const mutationObserver = new MutationObserver(schedule);

    mutationObserver.observe(benefits, {
      attributes: true,
      attributeFilter: ['class']
    });

    mutationObserver.observe(panel, {
      attributes: true,
      attributeFilter: ['hidden', 'class', 'style']
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(drawer);
      resizeObserver.observe(panel);
    }

    panel.addEventListener('transitionstart', schedule);
    panel.addEventListener('transitionend', schedule);

    drawer
      .querySelector('.menu-drawer__navigation-container')
      ?.addEventListener('scroll', schedule, { passive: true });

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, {
      passive: true
    });

    if (typeof MOBILE_QUERY.addEventListener === 'function') {
      MOBILE_QUERY.addEventListener('change', schedule);
    } else {
      MOBILE_QUERY.addListener(schedule);
    }

    schedule();
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(DRAWER_SELECTOR)) {
      initializeDrawer(scope);
    }

    scope
      .querySelectorAll?.(DRAWER_SELECTOR)
      .forEach(initializeDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll(),
      { once: true }
    );
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
